import { User, Entity, SystemStats, SystemHealth, EntityStatus } from '../types';

// Simulate API delay
const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

// Demo users data
const demoUsers: User[] = [
  {
    id: '1',
    username: 'admin',
    role: 'admin',
    is_active: true,
    created_at: '2024-01-01T00:00:00Z',
    email: 'admin@asknow.com',
    first_name: 'Admin',
    last_name: 'User',
  },
  {
    id: '2',
    username: 'user',
    role: 'user',
    is_active: true,
    created_at: '2024-01-02T00:00:00Z',
    email: 'user@asknow.com',
    first_name: 'Demo',
    last_name: 'User',
  },
  {
    id: '3',
    username: 'manager',
    role: 'manager',
    is_active: true,
    created_at: '2024-01-03T00:00:00Z',
    email: 'manager@asknow.com',
    first_name: 'Manager',
    last_name: 'User',
  },
  {
    id: '4',
    username: 'inactive_user',
    role: 'user',
    is_active: false,
    created_at: '2024-01-04T00:00:00Z',
    email: 'inactive@asknow.com',
    first_name: 'Inactive',
    last_name: 'User',
  },
];

// Demo entities data
const demoEntities: Entity[] = [
  {
    id: '1',
    name: 'Company Knowledge Base',
    description: 'Comprehensive knowledge base for company policies and procedures',
    status: EntityStatus.ACTIVE,
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-15T00:00:00Z',
    user_id: '1',
    is_processed: true,
    file_count: 15,
    url_count: 5,
  },
  {
    id: '2',
    name: 'Product Documentation',
    description: 'Technical documentation for all company products',
    status: EntityStatus.ACTIVE,
    created_at: '2024-01-05T00:00:00Z',
    updated_at: '2024-01-20T00:00:00Z',
    user_id: '2',
    is_processed: true,
    file_count: 8,
    url_count: 12,
  },
  {
    id: '3',
    name: 'Customer Support KB',
    description: 'Knowledge base for customer support representatives',
    status: EntityStatus.PROCESSING,
    created_at: '2024-01-10T00:00:00Z',
    updated_at: '2024-01-25T00:00:00Z',
    user_id: '3',
    is_processed: false,
    file_count: 25,
    url_count: 3,
  },
  {
    id: '4',
    name: 'Training Materials',
    description: 'Employee training and onboarding materials',
    status: EntityStatus.INACTIVE,
    created_at: '2024-01-15T00:00:00Z',
    updated_at: '2024-01-30T00:00:00Z',
    user_id: '1',
    is_processed: false,
    file_count: 0,
    url_count: 0,
  },
];

// Demo system stats
const demoSystemStats: SystemStats = {
  total_users: 4,
  active_users: 3,
  total_entities: 4,
  active_entities: 2,
  total_files: 48,
  total_urls: 20,
  system_health: 'healthy',
  last_updated: new Date().toISOString(),
};

// Demo system health
const demoSystemHealth: SystemHealth = {
  status: 'healthy',
  uptime: '99.9%',
  response_time: '120ms',
  error_rate: '0.1%',
  active_connections: 15,
  memory_usage: '45%',
  cpu_usage: '30%',
  disk_usage: '60%',
  last_check: new Date().toISOString(),
};

export const demoDataService = {
  // Users
  getUsers: async (): Promise<User[]> => {
    await delay(600);
    return demoUsers;
  },

  activateUser: async (userId: string): Promise<User> => {
    await delay(400);
    const user = demoUsers.find(u => u.id === userId);
    if (!user) throw new Error('User not found');
    
    user.is_active = true;
    return user;
  },

  deactivateUser: async (userId: string): Promise<User> => {
    await delay(400);
    const user = demoUsers.find(u => u.id === userId);
    if (!user) throw new Error('User not found');
    
    user.is_active = false;
    return user;
  },

  // Entities
  getEntities: async (): Promise<Entity[]> => {
    await delay(600);
    return demoEntities;
  },

  createEntity: async (entityData: any): Promise<Entity> => {
    await delay(800);
    const newEntity: Entity = {
      id: (demoEntities.length + 1).toString(),
      name: entityData.name,
      description: entityData.description,
      status: EntityStatus.ACTIVE,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      user_id: '1', // Demo admin user
      is_processed: false,
      file_count: 0,
      url_count: 0,
    };
    
    demoEntities.push(newEntity);
    return newEntity;
  },

  // System stats
  getSystemStats: async (): Promise<SystemStats> => {
    await delay(500);
    return demoSystemStats;
  },

  getSystemHealth: async (): Promise<SystemHealth> => {
    await delay(400);
    return demoSystemHealth;
  },
};
